/**
 * The guided-tour overlay: a dimmed backdrop with the current control cut out,
 * and one bubble explaining it.
 *
 * Rules this encodes, all of them deliberate:
 *  - It ADVANCES ON CLICK ONLY. The hints this replaces auto-dismissed after
 *    6 seconds, which meant a user who glanced away lost the message and, worse,
 *    a slow-mounting anchor could burn the whole hint on a race.
 *  - It runs ONE step at a time, in order, and only ends when the user ends it.
 *  - THERE IS NO "×". The product is new and the tour is how anyone finds out
 *    what the app does, so the way out is the one explicitly labelled "Skip
 *    tour" (plus Escape), which a user chooses rather than swats. A corner ×
 *    is the reflex click, and it ended the tour for people who had not yet read
 *    a word of it. Nothing here advances on a control that reads as "close" —
 *    an × wired to Next would be worse than no × at all.
 *  - A step whose anchor is missing is skipped rather than pointed at nothing.
 *  - Finishing or skipping marks the tour seen ONLY IF a step was actually
 *    drawn. A tour that never managed to render stands aside for the session
 *    and returns next visit — the first cut of this got that wrong and marked
 *    the main tour seen for an account that had never been shown it.
 *
 * Rendered in a portal at app level: the tab strip, the chart and the right rail
 * all clip overflow, so a bubble parented to the anchor would be cut off.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  deferTour,
  endTour,
  markTourSeen,
  nextStep,
  prevStep,
} from "@/state/slices/tourSlice";
import { anchorEl, stepsFor, type Placement, type TourStep } from "./tourSteps";
import styles from "./Tour.module.css";

/** Gap between the spotlight and the bubble. */
const GAP = 12;
/** Breathing room around the highlighted control. */
const PAD = 6;
/** Keep the bubble this far from the viewport edge. */
const MARGIN = 10;
const BUBBLE_W = 300;
/** How often to re-check which anchors exist, while a tour is on screen. A
 *  control can appear (a drawer opens) or go away mid-tour; cheap, since it is
 *  one `querySelector` per step. */
const RESCAN_MS = 400;
/** How long to tolerate a tour having NO anchored step before giving up on it
 *  for this session. Never marks it seen — see `giveUp`. */
const NO_ANCHOR_GRACE_MS = 4_000;

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

function place(
  anchor: Box,
  placement: Placement,
  bubble: { width: number; height: number },
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fits = {
    bottom: anchor.top + anchor.height + GAP + bubble.height <= vh - MARGIN,
    top: anchor.top - GAP - bubble.height >= MARGIN,
    right: anchor.left + anchor.width + GAP + bubble.width <= vw - MARGIN,
    left: anchor.left - GAP - bubble.width >= MARGIN,
  };
  // Preferred side first, then the opposite, then whatever fits.
  const opposite: Record<Placement, Placement> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
  };
  const order: Placement[] = [
    placement,
    opposite[placement],
    "bottom",
    "top",
    "right",
    "left",
  ];
  const side = order.find((s) => fits[s]) ?? "bottom";

  let top: number;
  let left: number;
  if (side === "bottom" || side === "top") {
    top =
      side === "bottom"
        ? anchor.top + anchor.height + GAP
        : anchor.top - GAP - bubble.height;
    left = anchor.left + anchor.width / 2 - bubble.width / 2;
  } else {
    left =
      side === "right"
        ? anchor.left + anchor.width + GAP
        : anchor.left - GAP - bubble.width;
    top = anchor.top + anchor.height / 2 - bubble.height / 2;
  }
  // Clamp into the viewport so a control near an edge still gets a full bubble.
  left = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - bubble.width - MARGIN));
  top = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - bubble.height - MARGIN));
  return { top, left };
}

export function Tour() {
  const dispatch = useAppDispatch();
  const active = useAppSelector((s) => s.tour.active);
  const step = useAppSelector((s) => s.tour.step);

  const [spot, setSpot] = useState<Box | null>(null);
  const [bubble, setBubble] = useState<{ top: number; left: number } | null>(null);
  const [bubbleEl, setBubbleEl] = useState<HTMLDivElement | null>(null);
  // Bumped on a timer while a tour runs, purely to re-derive `visible` below.
  const [scan, setScan] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setScan((n) => n + 1), RESCAN_MS);
    return () => window.clearInterval(id);
  }, [active]);

  // Which steps have a control on screen — DERIVED DURING RENDER, not held in
  // state. Holding it in state is what broke this the first time: the first
  // render after a tour starts saw the initial empty list, and the "no steps →
  // close" effect below fired against that stale value and marked the tour seen
  // before a single bubble had been drawn. Deriving it means it is never stale.
  const visible = useMemo<TourStep[]>(() => {
    if (!active) return [];
    return stepsFor(active).filter((s) => anchorEl(s) !== null);
    // `scan` is the re-measure trigger; the DOM is the real input here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, step, scan]);

  const total = visible.length;
  // Clamp: the list can shrink under us when a control goes away.
  const current = total > 0 ? visible[Math.min(step, total - 1)] : null;
  const isLast = step >= total - 1;

  // True once the user has actually been shown a step. Nothing may be marked
  // "seen" before this — a tour the user never saw must come back.
  const sawAStep = useRef(false);
  useEffect(() => {
    if (current && spot) sawAStep.current = true;
  }, [current, spot]);
  useEffect(() => {
    if (!active) sawAStep.current = false;
  }, [active]);

  /** Finished or dismissed BY THE USER — this is the only path that persists. */
  const finish = useCallback(() => {
    if (!active) return;
    if (sawAStep.current) {
      dispatch(endTour(active));
      dispatch(markTourSeen(active));
    } else {
      // Closing something that never rendered cannot count as having seen it.
      dispatch(deferTour(active));
    }
  }, [active, dispatch]);

  /** Nothing to point at. Stop for this session WITHOUT marking it seen, so it
   *  gets another chance next visit. */
  const giveUp = useCallback(() => {
    if (active) dispatch(deferTour(active));
  }, [active, dispatch]);

  // Track the anchor's rect for as long as the step is up, on every frame.
  //
  // Measuring once (plus resize/scroll listeners) was not enough: controls MOVE
  // without either event firing. The "+" tab button slides right as the saved
  // workspace hydrates its tabs, a drawer animates open, a pane divider is
  // dragged — all of which left the spotlight sitting where the control used to
  // be. A rAF loop is the only thing that is reliably right, and it is cheap:
  // one getBoundingClientRect per frame, and state is only written when the rect
  // actually changes, so it does not re-render on idle frames.
  useLayoutEffect(() => {
    if (!current) {
      setSpot(null);
      return;
    }
    let raf = 0;
    let last: Box | null = null;
    const same = (a: Box | null, b: Box | null) =>
      a !== null &&
      b !== null &&
      Math.abs(a.top - b.top) < 0.5 &&
      Math.abs(a.left - b.left) < 0.5 &&
      Math.abs(a.width - b.width) < 0.5 &&
      Math.abs(a.height - b.height) < 0.5;

    const tick = () => {
      const el = anchorEl(current);
      const next: Box | null = el
        ? (() => {
            const r = el.getBoundingClientRect();
            return {
              top: r.top - PAD,
              left: r.left - PAD,
              width: r.width + PAD * 2,
              height: r.height + PAD * 2,
            };
          })()
        : null;
      if (!same(next, last)) {
        last = next;
        setSpot(next);
      }
      raf = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(raf);
  }, [current]);

  // Position the bubble once we know how tall it actually is.
  useLayoutEffect(() => {
    if (!spot || !current) {
      setBubble(null);
      return;
    }
    const h = bubbleEl?.offsetHeight ?? 150;
    const w = bubbleEl?.offsetWidth ?? BUBBLE_W;
    setBubble(place(spot, current.placement, { width: w, height: h }));
  }, [spot, current, bubbleEl]);

  // Escape ends the tour — the same as Skip, and the shortcut people try first.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        finish();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, finish]);

  // No anchored step. Do NOT treat that as "seen": give the DOM a grace period
  // (controls mount asynchronously), then step aside for this session only.
  useEffect(() => {
    if (!active || total > 0) return;
    if (sawAStep.current) {
      // They saw the tour and the last control went away — that is a finish.
      finish();
      return;
    }
    const t = window.setTimeout(giveUp, NO_ANCHOR_GRACE_MS);
    return () => window.clearTimeout(t);
  }, [active, total, finish, giveUp]);

  if (!active || !current || !spot || total === 0) return null;

  return createPortal(
    <div className={styles.root} role="dialog" aria-modal="true" aria-label="Product tour">
      {/* Backdrop as four panels around the spotlight, so the highlighted
          control stays fully visible AND clickable-looking, while clicks on the
          dim area are absorbed (they would otherwise fire on the app behind). */}
      <div className={styles.mask} style={{ top: 0, left: 0, right: 0, height: Math.max(0, spot.top) }} />
      <div
        className={styles.mask}
        style={{ top: spot.top + spot.height, left: 0, right: 0, bottom: 0 }}
      />
      <div
        className={styles.mask}
        style={{ top: spot.top, left: 0, width: Math.max(0, spot.left), height: spot.height }}
      />
      <div
        className={styles.mask}
        style={{
          top: spot.top,
          left: spot.left + spot.width,
          right: 0,
          height: spot.height,
        }}
      />

      <div
        className={styles.spot}
        style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
        aria-hidden
      />

      <div
        ref={setBubbleEl}
        className={styles.bubble}
        style={{
          top: bubble?.top ?? spot.top + spot.height + GAP,
          left: bubble?.left ?? spot.left,
          // Hidden until placed, so it never flashes in the wrong spot.
          visibility: bubble ? "visible" : "hidden",
        }}
      >
        <div className={styles.head}>
          <span className={styles.count}>
            {step + 1} of {total}
          </span>
        </div>
        <h3 className={styles.title}>{current.title}</h3>
        <p className={styles.body}>{current.body}</p>
        <div className={styles.foot}>
          <button type="button" className={styles.skip} onClick={finish}>
            {isLast ? "Close" : "Skip tour"}
          </button>
          <div className={styles.nav}>
            {step > 0 ? (
              <button
                type="button"
                className={styles.back}
                onClick={() => dispatch(prevStep())}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              className={styles.next}
              onClick={() => (isLast ? finish() : dispatch(nextStep()))}
              autoFocus
            >
              {isLast ? "Got it" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
